// 业务"一次性批次"（听写/复习练习批次）专用的会话级存储。
//
// 选用 sessionStorage 的原因：
// 1. 当前批次本质是"页面间传递的一次性数据"，不需要跨会话长期持久化；
// 2. sessionStorage 关闭标签页自动清理，从根上杜绝长期累积导致的配额超限；
// 3. 多标签页天然隔离，避免不同练习互相串数据；
// 4. 刷新当前页面 / 浏览器内回退仍能拿到批次，满足"练习中误刷新不丢"的体验。
//
// 与上一轮"按前缀清理 localStorage"的兜底相比，本方案是治本——key 是固定的，
// 任何新批次都会覆盖旧批次，全局始终只保留 1 条记录。
//
// listKey 不再用作存储键，而是作为"批次时效标识"通过 URL 传递；读取时若
// 当前会话里的批次 listKey 不匹配，说明用户从历史链接进入，应当提示重新进入。

const BATCH_STORAGE_KEY = "current-batch";

// 历史遗留 key 的清理前缀。覆盖：
// - dictation-list-${ts}            （普通听写）
// - dictation-list-review-${ts}     （复习批次）
// - dictation-list-review-due-now-* （已到期复习批次）
// 不会误伤 dictationShuffleCurrentPage / dictationAutoSpeakOnFocus 等设置项
// （那些 key 不含 "-list-"）。
const LEGACY_BATCH_PREFIX = "dictation-list-";
const LEGACY_MIGRATION_FLAG = "current-batch:legacy-cleaned";

interface BatchEnvelope<T = unknown> {
  listKey: string;
  payload: T;
}

// 写入当前批次。固定 key 覆盖式写入。
// 返回 true 表示写入成功；失败通常只发生在 sessionStorage 被完全禁用的极端环境下。
export function saveBatch<T>(listKey: string, payload: T): boolean {
  if (!listKey) {
    console.warn("[batchStorage] saveBatch called without listKey");
    return false;
  }
  try {
    const envelope: BatchEnvelope<T> = { listKey, payload };
    sessionStorage.setItem(BATCH_STORAGE_KEY, JSON.stringify(envelope));
    return true;
  } catch (error) {
    console.error("[batchStorage] saveBatch failed", error);
    return false;
  }
}

// 读取当前批次。listKey 必须与写入时一致才返回 payload，
// 否则视为已过期（避免用户从旧链接进入读到错的批次）。
export function readBatch<T>(listKey: string): T | null {
  if (!listKey) return null;
  try {
    const raw = sessionStorage.getItem(BATCH_STORAGE_KEY);
    if (!raw) return null;
    const envelope = JSON.parse(raw) as BatchEnvelope<T> | null;
    if (!envelope || envelope.listKey !== listKey) return null;
    return (envelope.payload as T) ?? null;
  } catch (error) {
    console.error("[batchStorage] readBatch failed", error);
    return null;
  }
}

// 清除当前批次。供"练习完成 / 退出"等显式场景调用，
// 关闭标签页时浏览器也会自动清理，调用方可以省略。
export function clearBatch(): void {
  try {
    sessionStorage.removeItem(BATCH_STORAGE_KEY);
  } catch (error) {
    console.error("[batchStorage] clearBatch failed", error);
  }
}

// 一次性迁移：把历史遗留在 localStorage 里的批次缓存清理掉。
// 仅命中 `dictation-list-` 前缀，不影响其它持久化设置。
// 通过一个一次性 flag 防止重复执行。
export function migrateLegacyBatches(): void {
  try {
    if (localStorage.getItem(LEGACY_MIGRATION_FLAG) === "1") return;
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(LEGACY_BATCH_PREFIX)) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch (innerError) {
        console.error(
          `[batchStorage] failed to remove legacy key ${key}`,
          innerError,
        );
      }
    });
    localStorage.setItem(LEGACY_MIGRATION_FLAG, "1");
  } catch (error) {
    console.error("[batchStorage] migrateLegacyBatches failed", error);
  }
}
